"use client"

import { createElement, Fragment, useId, useState, type ReactNode } from "react"
import { ChevronDownIcon } from "@/components/brand-kit/icon-sprite"

interface FAQItem {
  question: string
  answer: string
}

interface FAQAccordionProps {
  /** List of question/answer pairs */
  items: FAQItem[]
  /** Number of grid columns (1 or 2, defaults to 2) */
  columns?: 1 | 2
  /** Indices of items open by default */
  defaultOpen?: number[]
  /** Section title (defaults to "Frequently Asked Questions") */
  title?: string
  /** Whether to show a top border separator */
  bordered?: boolean
  /** Additional classes on the wrapper */
  className?: string
}

type AnswerFrame = {
  tag: "root" | "a" | "strong"
  href?: string
  children: ReactNode[]
}

const FAQ_ENTITY: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: "\u00a0",
  quot: '"',
}

function decodeFaqEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|amp|apos|gt|lt|nbsp|quot);/gi, (entity, name: string) => {
    if (name[0] !== "#") return FAQ_ENTITY[name.toLowerCase()] ?? entity
    const radix = name[1]?.toLowerCase() === "x" ? 16 : 10
    const digits = radix === 16 ? name.slice(2) : name.slice(1)
    const codePoint = Number.parseInt(digits, radix)
    if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) return entity
    try {
      return String.fromCodePoint(codePoint)
    } catch {
      return entity
    }
  })
}

function safeFaqHref(tag: string): string | null {
  const match = tag.match(/\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i)
  const href = decodeFaqEntities(match?.[1] ?? match?.[2] ?? match?.[3] ?? "").trim()
  if (!href) return null
  if (href.startsWith("/") && !href.startsWith("//")) return href
  if (/^https?:\/\//i.test(href)) return href
  return null
}

/**
 * FAQ answers have one small rich-text contract: links and bold text. Parse
 * that contract into React nodes so stored HTML never reaches an HTML injection
 * sink. Unknown tags and attributes are discarded; React escapes every text
 * node. This preserves the live FAQ census while keeping a full HTML sanitizer
 * out of the first-load client bundle.
 */
export function renderFaqAnswer(answer: string): ReactNode {
  const frames: AnswerFrame[] = [{ tag: "root", children: [] }]
  const tokens = answer.split(/(<[^>]*>)/g).filter(Boolean)
  let key = 0

  const append = (node: ReactNode) => frames[frames.length - 1].children.push(node)
  const close = (tag: "a" | "strong") => {
    const frame = frames[frames.length - 1]
    if (frame.tag !== tag) return
    frames.pop()
    const children = frame.children
    if (tag === "strong") append(createElement("strong", { key: key++ }, children))
    else if (frame.href) append(createElement("a", { href: frame.href, key: key++ }, children))
    else children.forEach(append)
  }

  for (const token of tokens) {
    if (!token.startsWith("<")) {
      append(decodeFaqEntities(token))
      continue
    }
    if (/^<strong\b/i.test(token)) frames.push({ tag: "strong", children: [] })
    else if (/^<a\b/i.test(token)) frames.push({ tag: "a", href: safeFaqHref(token) ?? undefined, children: [] })
    else if (/^<\/strong\s*>/i.test(token)) close("strong")
    else if (/^<\/a\s*>/i.test(token)) close("a")
    else if (/^<br\s*\/?\s*>/i.test(token)) append(createElement("br", { key: key++ }))
  }

  while (frames.length > 1) close(frames[frames.length - 1].tag as "a" | "strong")
  return createElement(Fragment, null, frames[0].children)
}

export function FAQAccordion({
  items,
  columns = 2,
  defaultOpen = [],
  title = "Frequently Asked Questions",
  bordered = true,
  className = "",
}: FAQAccordionProps) {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set(defaultOpen))
  const idBase = useId()

  const toggle = (index: number) => {
    const next = new Set(openItems)
    if (next.has(index)) {
      next.delete(index)
    } else {
      next.add(index)
    }
    setOpenItems(next)
  }

  const gridClass = columns === 2 ? "grid md:grid-cols-2 gap-4" : "flex flex-col gap-4"

  return (
    <section className={`mt-12 ${bordered ? "pt-10 border-t border-gray-200" : ""} ${className}`}>
      {title && <h2 className="text-xl font-bold tracking-tight text-gray-900 mb-6">{title}</h2>}
      <div className={gridClass}>
        {items.map((item, i) => {
          const isOpen = openItems.has(i)
          const panelId = `${idBase}faq-${i}`
          return (
            <div
              key={i}
              className={`rounded-lg overflow-hidden transition-colors ${
                isOpen ? "bg-[#0469f0]/[0.05]" : "bg-[#0469f0]/[0.02]"
              }`}
            >
              <button
                type="button"
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="w-full p-5 flex items-center justify-between text-left cursor-pointer"
              >
                <span className="font-bold text-gray-900 text-sm pr-4">{item.question}</span>
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {/* FRIDGE-1441: every answer is in the server HTML. A closed one is
                  hidden, not absent, so crawlers and AI fetchers read the same
                  answers the FAQPage node carries. */}
              <div id={panelId} hidden={!isOpen} className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">
                {renderFaqAnswer(item.answer)}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
